import React, { createContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const CartContext = createContext();

// Helper: Manage guest cart in localStorage
const GUEST_CART_KEY = 'doctape_guest_cart';

const getGuestCart = () => {
    try {
        return JSON.parse(localStorage.getItem(GUEST_CART_KEY)) || { items: [] };
    } catch {
        return { items: [] };
    }
};

const saveGuestCart = (cart) => {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
};

const clearGuestCart = () => {
    localStorage.removeItem(GUEST_CART_KEY);
};

export const CartProvider = ({ children }) => {
    const [cart, setCart] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isGuest, setIsGuest] = useState(true); // Track if user is guest

    const token = localStorage.getItem('token');
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

    const getCartConfig = useCallback(() => ({
        headers: {
            Authorization: `Bearer ${token}`
        }
    }), [token]);

    // Fetch cart from server (authenticated users)
    const fetchServerCart = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${apiBaseUrl}/cart`, getCartConfig());
            setCart(response.data);
            setIsGuest(false);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch cart');
            setIsGuest(true);
        } finally {
            setLoading(false);
        }
    }, [token, apiBaseUrl, getCartConfig]);

    // Initialize cart on mount
    useEffect(() => {
        if (token) {
            // User is logged in, fetch from server
            fetchServerCart();
        } else {
            // Guest user, load from localStorage
            setIsGuest(true);
            setCart(getGuestCart());
        }
    }, [token, fetchServerCart]);

    // A guest line is identified by product AND variant, so the same product in
    // two sizes stays two lines rather than merging into one quantity.
    const matchesLine = (item, productId, variantId = null) =>
        (item.productId === productId || item.productId?._id === productId) &&
        (item.variantId || null) === (variantId || null);

    // Guest: Add to local cart
    const addGuestCartItem = useCallback((productId, quantity = 1, productData, variantId = null) => {
        const guestCart = getGuestCart();
        const existingItem = guestCart.items.find(item => matchesLine(item, productId, variantId));

        if (existingItem) {
            existingItem.quantity += quantity;
        } else {
            const variant = productData?.variants?.find(v => v._id === variantId);
            guestCart.items.push({
                productId: productData || productId,
                quantity,
                variantId: variantId || null,
                variantLabel: variant?.label,
                // Priced from the variant when there is one, so the guest cart
                // total matches what the server will charge.
                unitPrice: variant ? variant.price : productData?.price,
                _id: Math.random().toString(36).substr(2, 9)
            });
        }

        saveGuestCart(guestCart);
        setCart(guestCart);
    }, []);

    // Guest: Remove from local cart
    const removeGuestCartItem = useCallback((productId, variantId = null) => {
        const guestCart = getGuestCart();
        guestCart.items = guestCart.items.filter(item => !matchesLine(item, productId, variantId));
        saveGuestCart(guestCart);
        setCart(guestCart);
    }, []);

    // Guest: Update cart item
    const updateGuestCartItem = useCallback((productId, quantity, variantId = null) => {
        const guestCart = getGuestCart();
        const item = guestCart.items.find(item => matchesLine(item, productId, variantId));

        if (item) {
            if (quantity <= 0) {
                guestCart.items = guestCart.items.filter(i => i._id !== item._id);
            } else {
                item.quantity = quantity;
            }
        }
        
        saveGuestCart(guestCart);
        setCart(guestCart);
    }, []);

    const addToCart = useCallback(async (productId, quantity = 1, variantId = null) => {
        try {
            if (isGuest || !token) {
                // Fetch product data for guest
                const response = await axios.get(`${apiBaseUrl}/products/${productId}`);
                addGuestCartItem(productId, quantity, response.data, variantId);
            } else {
                // Server cart for authenticated users
                const response = await axios.post(
                    `${apiBaseUrl}/cart/add`,
                    { productId, quantity, variantId },
                    getCartConfig()
                );
                setCart(response.data);
            }
            return true;
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add to cart');
            return false; // reported rather than thrown, so existing callers
                          // that ignore the result do not see a rejection
        }
    }, [apiBaseUrl, getCartConfig, isGuest, token, addGuestCartItem]);

    const removeFromCart = useCallback(async (productId, variantId = null) => {
        try {
            if (isGuest || !token) {
                removeGuestCartItem(productId, variantId);
            } else {
                const response = await axios.delete(
                    `${apiBaseUrl}/cart/${productId}${variantId ? `?variantId=${variantId}` : ''}`,
                    getCartConfig()
                );
                setCart(response.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to remove from cart');
        }
    }, [apiBaseUrl, getCartConfig, isGuest, token, removeGuestCartItem]);

    const updateCartItem = useCallback(async (productId, quantity, variantId = null) => {
        try {
            if (isGuest || !token) {
                updateGuestCartItem(productId, quantity, variantId);
            } else {
                const response = await axios.put(
                    `${apiBaseUrl}/cart/${productId}`,
                    { quantity, variantId },
                    getCartConfig()
                );
                setCart(response.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update cart');
        }
    }, [apiBaseUrl, getCartConfig, isGuest, token, updateGuestCartItem]);

    const clearCart = useCallback(async () => {
        try {
            if (isGuest || !token) {
                clearGuestCart();
                setCart({ items: [] });
            } else {
                const response = await axios.delete(`${apiBaseUrl}/cart`, getCartConfig());
                setCart(response.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to clear cart');
        }
    }, [apiBaseUrl, getCartConfig, isGuest, token]);

    const value = {
        cart,
        loading,
        error,
        addToCart,
        removeFromCart,
        updateCartItem,
        clearCart,
        fetchCart: fetchServerCart,
        isGuest
    };

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
